import React, { useMemo } from "react";
import { useTheme, Theme } from '../contexts/ThemeContext';

// RN-ALIGNED: Clean createStyles function matching React Native pattern
const createStyles = (theme: Theme) => ({
  container: {
    maxWidth: '672px', // 2xl equivalent (42rem)
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: theme.spacing.xl,
    color: theme.colors.text,
  },
  title: {
    fontSize: theme.typography.sizes.xxxl, // 3xl equivalent
    fontWeight: theme.typography.weights.bold,
    marginBottom: theme.spacing.lg,
    color: theme.colors.text,
  },
  effectiveDate: {
    marginBottom: theme.spacing.sm,
    fontWeight: theme.typography.weights.semiBold,
    color: theme.colors.text,
  },
  sectionTitle: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: theme.typography.weights.semiBold,
    marginTop: theme.spacing.xl,
    marginBottom: theme.spacing.sm,
    color: theme.colors.text,
  },
  paragraph: {
    marginBottom: theme.spacing.sm,
    lineHeight: theme.typography.lineHeights.relaxed,
    color: theme.colors.text,
  },
  list: {
    marginLeft: theme.spacing.xl,
    marginBottom: theme.spacing.sm,
    listStyleType: 'disc',
    color: theme.colors.text,
  },
  listItem: {
    marginBottom: theme.spacing.xs,
    lineHeight: theme.typography.lineHeights.relaxed,
  },
  link: {
    color: theme.colors.primary,
    textDecoration: 'underline',
  },
  strong: {
    fontWeight: theme.typography.weights.semiBold,
  },
  separator: {
    marginVertical: theme.spacing.xl,
    borderTopWidth: 1,
    borderTopStyle: 'solid' as const,
    borderTopColor: theme.colors.border,
  },
  footer: {
    fontStyle: 'italic',
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  contactParagraph: {
    marginBottom: theme.spacing.xl,
    color: theme.colors.text,
  },
});

const PrivacyPolicy: React.FC = () => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Privacy Policy</h1>
      <p style={styles.effectiveDate}>Effective Date: 01/03/2026</p>
      
      <h2 style={styles.sectionTitle}>Overview</h2>
      <p style={styles.paragraph}>
        ChordBaker is committed to protecting your privacy. This Privacy Policy explains how we handle information when you use our website at{' '}
        <a 
          href="https://chordbaker.com/" 
          style={styles.link}
        >
          https://chordbaker.com/
        </a>
        .
      </p>
      
      <h2 style={styles.sectionTitle}>Information We Don't Collect</h2>
      <p style={styles.paragraph}>ChordBaker does not collect, store, process, or transmit any personal information. Specifically:</p>
      <ul style={styles.list}>
        <li style={styles.listItem}>We do not collect names, email addresses, or contact information</li>
        <li style={styles.listItem}>We do not collect IP addresses or device identifiers</li>
        <li style={styles.listItem}>We do not store any files, documents, or user-generated content (your chord charts remain entirely on your device)</li>
        <li style={styles.listItem}>We do not record your screen or capture session replays</li>
      </ul>
      
      <h2 style={styles.sectionTitle}>Third-Party Services</h2>
      <p style={styles.paragraph}>We use the following third-party services. We recommend reviewing their privacy policies:</p>
      <ul style={styles.list}>
        <li style={styles.listItem}>
          <span style={styles.strong}>Web Hosting Provider:</span> Google Cloud Computing: https://cloud.google.com/?hl=en
        </li>
        <li style={styles.listItem}>
          <span style={styles.strong}>Analytics:</span> PostHog — used for anonymous usage analytics as described in the Analytics section below. Privacy policy:{' '}
          <a href="https://posthog.com/privacy" style={styles.link}>https://posthog.com/privacy</a>
        </li>
      </ul>
      
      <h2 style={styles.sectionTitle}>External Links</h2>
      <p style={styles.paragraph}>Our website may contain links to external websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies before providing any information.</p>
      
      <h2 style={styles.sectionTitle}>Analytics</h2>
      <p style={styles.paragraph}>We use PostHog to collect anonymous usage analytics that help us understand which features are used and improve the application. This data includes:</p>
      <ul style={styles.list}>
        <li style={styles.listItem}>Feature interactions (e.g., exporting a chart, toggling display options, changing themes)</li>
        <li style={styles.listItem}>General usage patterns (e.g., how often sections are added or reordered)</li>
        <li style={styles.listItem}>Error occurrences (e.g., failed imports)</li>
      </ul>
      <p style={styles.paragraph}>This analytics data is anonymous and does not include any personal information, chord chart content, lyrics, or custom text you enter. We do not use session recording. PostHog respects the Do Not Track browser setting.</p>
      
      <h2 style={styles.sectionTitle}>Data Security</h2>
      <p style={styles.paragraph}>We do not collect or store any personal information. The anonymous analytics data described above is processed by PostHog and does not contain any personally identifiable information.</p>
      
      <h2 style={styles.sectionTitle}>Children's Privacy</h2>
      <p style={styles.paragraph}>Our website does not collect personal information from anyone, including children under 13. The anonymous analytics data collected applies equally to all users.</p>
      
      <h2 style={styles.sectionTitle}>Changes to This Policy</h2>
      <p style={styles.paragraph}>We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date.</p>
      
      <h2 style={styles.sectionTitle}>Contact Information</h2>
      <p style={styles.paragraph}>If you have any questions about this Privacy Policy, please contact us at:</p>
      <p style={styles.contactParagraph}>tomas@chordbaker.com</p>
      
      <div style={styles.separator} />
      
      <p style={styles.footer}>This policy reflects our commitment to user privacy. We only collect minimal anonymous analytics to improve the application.</p>
    </div>
  );
};

export default PrivacyPolicy;